
import { ArrayUtil, ValueDisplayValue } from "@mcleod/core";
import { ImageNames } from "@mcleod/images";
import { AddType, DesignerInterface, DialogProps, Label, PanelProps, TableProps, TableRow } from "@mcleod/components";
import { AbstractValueDisplayValueEditor } from "./AbstractValueDisplayValueEditor";

export class RadioGroupItemEditor extends AbstractValueDisplayValueEditor {
    private _isReadOnly: boolean;

    constructor(currentValueJson: string, editingDesigner: DesignerInterface, props?: Partial<PanelProps>) {
        super(currentValueJson, props, null, editingDesigner);
        if (this.isReadOnly) {
            const readOnlyLabel = new Label({
                text: "Radio options are system-defined and cannot be edited",
                fontSize: "large",
                imageName: ImageNames.triangleWarning,
                color: "warning",
                paddingBottom: 24
            });
            this.insert(readOnlyLabel, 0);
        }
    }

    protected get editingDesigner(): DesignerInterface {
        return super.editingDesigner;
    }

    protected set editingDesigner(value: DesignerInterface) {
        super.editingDesigner = value;

        this.isReadOnly = this.editingDesigner?.selectedComponents.some(
            (comp) => comp["_boundField"]?.dbDisplayValues != null
        );
    }

    protected get isReadOnly(): boolean {
        return this._isReadOnly;
    }

    private set isReadOnly(value: boolean) {
        this._isReadOnly = value;
    }

    override getTableProps(): Partial<TableProps> {
        if (this.isReadOnly) {
            return {
                ...super.getTableProps(),
                addType: AddType.NOT_ALLOWED,
                allowEdit: false,
                allowDelete: false
            }
        }
        return super.getTableProps();
    }

    override getDialogProps(): Partial<DialogProps> {
        return {
            ...super.getDialogProps(),
            title: "Radio Option Setup",
            okVisible: !this.isReadOnly,
            okDefault: !this.isReadOnly
        }
    }

    get valueCaption(): string {
        return "Value";
    }

    get valueName(): string {
        return "value";
    }

    get displayValueCaption(): string {
        return "Caption";
    }

    get displayValueName(): string {
        return "displayValue";
    }

    get emptyCaption(): string {
        return "The database value / display value pairs that represent radio options can be defined in this table";
    }

    getResultValue(): any[] {
        return (ArrayUtil.isEmptyArray(this.tableRows)) ? null :
            this.tableRows.map(row => this.getItemFromTableRow(row));
    }

    protected getItemFromTableRow(row: TableRow): ValueDisplayValue {
        return new ValueDisplayValue(row.data.get(this.valueName, null),
            row.data.get(this.displayValueName, null));
    }
}
